'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { CircularProgress, PulseButton, Search } from 'src/components';
import { StrategyTable, SortField, SortDirection } from '../StrategyTable';
import { StrategyDeleteModal } from '../StrategyDeleteModal';
import {
  useGetAllEngageStrategiesQuery,
  useDeleteEngageStrategyMutation,
} from 'src/api/redux';
import { useChainMap } from '../../hooks';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { LOCATION } from 'src/hooks/filters/constants';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDebounce } from 'use-debounce';

const EngageStrategiesGrid = () => {
  const router = useRouter();
  const log = useLogging();
  const { setNotification } = useNotification();

  // Search state
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Build query params for server-side sorting and searching
  const queryParams = {
    ...(debouncedSearch && { q: debouncedSearch }),
    sort: sortField,
    order: sortDirection,
  };

  const { data: engageStrategies, isLoading } = useGetAllEngageStrategiesQuery(
    queryParams,
    {
      pollingInterval: 60_000,
    }
  );
  const [deleteStrategy, { isLoading: isDeleting }] =
    useDeleteEngageStrategyMutation();

  const chainMap = useChainMap('engage');
  const { selectedStrategy, setSelectedStrategy } = useStrategies(
    LOCATION.STRATEGY_MANAGER
  );

  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleSort = (field: SortField, direction: SortDirection) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleAddStrategy = () => {
    log.event('Add strategy button clicked', {
      source: 'EngageStrategiesGrid',
      strategyMode: 'engage',
    });
    router.push('/settings/admin/strategy-manager/engage/add');
  };

  const handleEditStrategy = (id: string) => {
    const strategy = engageStrategies?.results?.find(
      (s) => s.id.toString() === id
    );

    log.event('Edit strategy clicked', {
      source: 'EngageStrategiesGrid',
      strategyId: id,
      strategyLabel: strategy?.label,
      strategyType: strategy?.type,
      isEngage: true,
    });

    router.push(`/settings/admin/strategy-manager/engage/${id}/edit`);
  };

  const handleCopyStrategy = (id: string) => {
    const strategy = engageStrategies?.results?.find(
      (s) => s.id.toString() === id
    );

    log.event('Copy strategy clicked', {
      source: 'EngageStrategiesGrid',
      strategyId: id,
      strategyLabel: strategy?.label,
      strategyType: strategy?.type,
      isEngage: true,
    });

    router.push(`/settings/admin/strategy-manager/engage/copy/${id}`);
  };

  const handleShowDialog = (strategy) => {
    setStrategyToDelete(strategy);
    setShowDeleteDialog(true);
  };

  const handleClose = () => {
    setStrategyToDelete(null);
    setShowDeleteDialog(false);
  };

  const handleDeleteStrategy = async (id: string) => {
    const strategy = engageStrategies?.results?.find(
      (s) => s.id.toString() === id
    );
    try {
      await deleteStrategy(id);
      log.event('Strategy deleted', {
        source: 'EngageStrategiesGrid',
        strategyId: id,
        strategyLabel: strategy?.label,
        strategyType: strategy?.type,
        isEngage: true,
      });
      setNotification({
        title: 'Strategy deleted',
        message: 'Strategy deleted successfully',
        type: 'success',
      });
    } catch (err) {
      log.exception(`Error deleting strategy: ${err.data?.error_message}`);
      setNotification({
        title: 'Error deleting strategy',
        message: err?.data?.error_message,
        type: 'error',
      });
    } finally {
      setShowDeleteDialog(false);
      setStrategyToDelete(null);
      if (selectedStrategy?.id?.toString() === id) {
        setSelectedStrategy(null);
      }
    }
  };

  const isAnyLoading = isLoading || isDeleting;

  return (
    <>
      <div className="relative space-y-4">
        {/* Add New Strategy Button */}
        <div className="flex justify-between">
          <Search
            value={search}
            onSearch={setSearch}
            size="sm"
            placeholder="Search strategies..."
            className="rounded-full min-w-64"
          />
          <PulseButton
            variant="solid"
            color="primary"
            size="sm"
            startDecorator={faPlus}
            onClick={handleAddStrategy}
          >
            New Strategy
          </PulseButton>
        </div>

        {/* Strategy Table */}
        <div
          className={`transition-all duration-200 ${
            isAnyLoading ? 'blur-sm pointer-events-none' : ''
          }`}
        >
          <StrategyTable
            strategies={engageStrategies?.results || []}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onRowClick={(strategy) =>
              handleEditStrategy(strategy.id.toString())
            }
            onEdit={(strategy) => handleEditStrategy(strategy.id.toString())}
            onDelete={(strategy) => handleShowDialog(strategy)}
            onCopy={(strategy) => handleCopyStrategy(strategy.id.toString())}
            chainMap={chainMap}
          />
        </div>

        {/* Loading overlay */}
        {isAnyLoading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white/10 backdrop-blur-sm">
            <CircularProgress size="md" color="neutral" />
          </div>
        )}
      </div>

      <StrategyDeleteModal
        open={showDeleteDialog}
        onClose={handleClose}
        onDelete={handleDeleteStrategy}
        strategy={strategyToDelete}
      />
    </>
  );
};

export default EngageStrategiesGrid;
